<template lang="pug">
div
  .text-2xl.font-semibold.mb-4(v-if="!hideTitle") Inventory
  v-row(no-gutters)
    v-col.mr-4(cols="8")
      v-card.border.rounded-lg.pa-6(outlined height="100%")
        v-card.rounded-lg.pa-4(outlined)
          v-row(no-gutters)
            v-col
              .d-flex.align-center.mb-2.justify-space-between
                div.d-flex
                  v-icon.mr-2(size="15" color="success500") $yzi-check-circle-solid
                  .text-xs.font-medium In stock
                v-tooltip(top color="black" content-class="black tooltip-top-arrow" max-width="300px")
                  template(#activator="{ on, attrs }")
                    v-icon(size="15" v-bind="attrs" v-on="on") $yzi-info-square
                  span Items currently available in your inventory with positive balance
              div.danger500--text(v-if="purchasesMetaData.isError || purchasesMetaData.data.inStock === null")
                .text-xl.font-semibold
                  v-icon.mr-2(size="15" color="danger500") mdi-cancel
                  span inventories
                .text-xxs Error fetching data
              .text-xl.d-flex.align-center.font-gotham-rounded.font-weight-350(v-else)
                template(v-if="purchasesMetaData.isFetching")
                  v-progress-circular(indeterminate size="15" color="primary" width="2")
                template(v-else)
                  div {{ purchasesMetaData.data.inStock }} 
                span.ml-2 inventories
            v-divider.mx-5(vertical)
            v-col
              .d-flex.align-center.mb-2.justify-space-between
                div.d-flex
                  v-icon.mr-2(size="15" color="danger500") $yzi-alert-circle
                  .text-xs.font-medium Out of stock
                v-tooltip(top color="black" content-class="black tooltip-top-arrow" max-width="300px")
                  template(#activator="{ on, attrs }")
                    v-icon(size="15" v-bind="attrs" v-on="on") $yzi-info-square
                  span Inventory depleted with no current balance available
              div.danger500--text(v-if="purchasesMetaData.isError || purchasesMetaData.data.noStock === null")
                .text-xl.font-semibold
                  v-icon.mr-2(size="15" color="danger500") mdi-cancel
                  span inventories
                .text-xxs Error fetching data
              .text-xl.d-flex.align-center.font-gotham-rounded.font-weight-350(v-else)
                template(v-if="purchasesMetaData.isFetching")
                  v-progress-circular(indeterminate size="15" color="primary" width="2")
                template(v-else)
                  div {{ purchasesMetaData.data.noStock }} 
                span.ml-2 inventories
            v-divider.mx-5(vertical)
            v-col
              .d-flex.align-center.mb-2.justify-space-between
                div.d-flex
                  v-icon.mr-2(size="15" color="warning500") $yzi-hourglass-02 
                  .text-xs.font-medium Order soon
                v-tooltip(top color="black" content-class="black tooltip-top-arrow" max-width="300px")
                  template(#activator="{ on, attrs }")
                    v-icon(size="15" v-bind="attrs" v-on="on") $yzi-info-square
                  span Inventory predicted to run out in under 14 days based on current usage trends and stock balance
              div.danger500--text(v-if="purchasesMetaData.isError || purchasesMetaData.data.orderSoon === null")
                .text-xl.font-semibold
                  v-icon.mr-2(size="15" color="danger500") mdi-cancel
                  span inventories
                .text-xxs Error fetching data
              .text-xl.d-flex.align-center.font-gotham-rounded.font-weight-350(v-else)
                template(v-if="purchasesMetaData.isFetching")
                  v-progress-circular(indeterminate size="15" color="primary" width="2")
                div(v-else)
                  div {{ purchasesMetaData.data.orderSoon }} 
                span.ml-2 inventories
        inventory-list-tab.mt-3(v-model="selectedInventoryList" :show-search-field="showSearchField")
          template(#search)
            v-btn(@click="clickSearch" icon color="neutral500")
              v-icon(size="20") $yzi-search-sm
            v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat :placeholder="$t('type_to_search')" hide-details clearable autofocus)
        v-data-table.mt-2.pb-6(v-model="selected" :headers="headers" :items="inventories.data" item-key="id" :items-per-page="inventories.pageSize" :loading="inventories.isFetching" hide-default-footer loader-height="2" show-select)
          template(v-slot:header.data-table-select="{ props }")
            yc-checkbox(:input-value="allSelected" :indeterminate="props.indeterminate" :disabled="inventories.data.length === 0" @change="handleSelectAll")
          template(v-slot:item.data-table-select="{ item, select, isSelected }")
            yc-checkbox(:input-value="isSelected" :disabled="item.disabled" @change="select($event)")
          template(v-slot:item.name="{ item }")
            .py-2
              .d-flex.align-center.justify-space-between
                div {{ item.name }}
                yz-badge(v-if="isHqContext || item.detail.branch_for" x-small color="blue-100" text-color="blue-500" border) HQ
              .d-flex.align-center
                .text-xxs.neutral500--text.mr-4 UOM: {{ item.uom }}
                .text-xxs.neutral500--text LAST PRICE: {{ item.lastPrice | displayPrice }}
          template(v-slot:item.available_qty="{ item }")
            .py-2
              .d-flex.align-center
                div {{ item.available_qty }}
                yz-tooltip(attach)
                  template(#tooltip)
                    div On hand: {{ item.detail.inventory_qty || 0 }}
                    div Available: {{ item.detail.available_qty || 0 }}
                    div Reserved: {{ item.detail.reserved_qty || 0 }}
                    div Committed: {{ item.detail.committed_qty || 0 }}
                    div Incoming: {{ item.detail.incoming_qty || 0 }}
                    div To order: {{ item.detail.to_order_qty || 0 }}
                  yz-icon.ml-2.mb-1(size="14" color="neutral500") $yzi-info-circle
              .text-xxs(:class="[getInventoryStatusTextColor(item.inventory_qty, item.last14d)]") {{ getInventoryStatus(item.inventory_qty, item.last14d) }}

        div.d-flex.justify-end(v-if="inventories.totalCount > inventories.pageSize")
          yc-pagination-custom(v-model="inventories.currentPage" :parentPageSize.sync="inventories.pageSize" :count="inventories.totalCount" attach menu-top @input="inventoriesPageChanged")
    v-col
      v-card.border.rounded-lg(outlined height="100%")
        .pa-4
          v-row(no-gutters)
            v-col
              .text-sm.font-semibold.mb-4 Selected item
            v-spacer
            v-col.d-flex.justify-end(v-if="selected.length > 0")
              v-btn.button-xs.neutral700--text.underline.mr-1(text @click="handleRemoveAllItem") Remove all
          template(v-if="selected.length === 0")
            v-card.px-4.py-8.border-dashed(outlined)
              .d-flex.flex-column.align-center.justify-center
                v-icon.mb-6(size="30") $yzi-colors
                .text-md.font-semibold.mb-1 No items selected
                .text-sm.neutral500--text Any selected items from your list will show up here
          div.overflow-selection(v-else)
            div(v-for="(selectedItem, selectedItemIdx) in selected" :key="selectedItemIdx")
              v-row.pa-2.align-center(no-gutters)
                v-col(cols="2")
                  .text-sm {{ selectedItemIdx + 1 }}
                v-col.mx-2
                  .text-sm {{ selectedItem.name }}
                v-col.d-flex.justify-end(cols="2")
                  v-btn.button-xs.danger700--text.px-0.underline(text @click="handleRemoveItem(selectedItem)") Remove
              v-divider(v-if="selectedItemIdx !== selected.length - 1")
        div(:class="[{ 'to-bottom': inventories.totalCount > 1 }]")
          v-divider
          .text-sm.font-semibold.px-4.pt-3 Create a new:
          v-row.px-4.py-3(no-gutters)
            v-col.mr-2
              v-btn.button-sm.neutral700.white--text(block depressed :disabled="selected.length === 0" @click="handleCreate('quotation')") Quotation
            v-col
              v-btn.button-sm.neutral700.white--text(block depressed :disabled="selected.length === 0" @click="handleCreate('purchase-order')") Purchase order
  .dialogs
    add-edit-document-dialog(v-if="openDocumentDialog" :open.sync="openDocumentDialog" :selectedDocument="selectedDocument" :injectItems="injectItems" @updated="handleUpdatedDocument")
</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import AddEditDocumentDialog from '@/components/clinic/dialogs/purchasehub/AddEditDocumentDialog.vue'
import InventoryListDropdown from '@/components/clinic/purchasehub/InventoryListDropdown.vue'
import InventoryListTab from '@/components/clinic/purchasehub/InventoryListTab.vue'

export default {
  components: {
    AddEditDocumentDialog, InventoryListDropdown, InventoryListTab
  },
  props: {
    purchasesMetaData: {
      type: Object,
      required: true
    },
    locationFilter: {
      type: Array,
      default: null
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      selected: [],
      inventories: {
        meta: {
          outOfStock: null,
          orderSoon: null,
        },
        data: [],
        isFetching: false,
        isError: false,
        totalCount: 0,
        currentPage: 1,
        pageSize: 10,
      },
      showSearchField: false,
      searchText: null,
      selectedInventoryList: null,
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Available', value: 'available_qty',  sortable: true },
        { text: 'To order', value: 'to_order_qty' },
        { text: 'Last 7D', value: 'last7d' },
        { text: 'Last 14D', value: 'last14d' },
        { text: 'Last 30D', value: 'last30d' },
      ],
      openDocumentDialog: false,
      selectedDocument: null
    }
  },
  mounted () {
    this.fetchInventories()
  },
  watch: {
    selectedInventoryList () {
      this.inventories.currentPage = 1
      this.fetchInventories()
    },
    locationFilter () {
      this.inventories.currentPage = 1
      this.fetchInventories()
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      isHqContext: 'hq/getIsHq'
    }),
    allSelected () {
      const productIds = this.inventories.data.map(item => item.id)
      return this.selected.filter(item => productIds.includes(item.id)).length === productIds.length
    },
    inventoriesParams () {
      const commonParams = {
        page: this.inventories.currentPage,
        page_size: this.inventories.pageSize,
        status: 'published',
      }
      if (this.searchText) {
        commonParams.search = this.searchText
      }
      if (this.locationFilter && this.locationFilter.length > 0) {
        commonParams.companies = this.locationFilter.join(',')
      }
      if (this.selectedInventoryList?.id === 'all') {
        return commonParams
      }
      if (this.selectedInventoryList?.id === 'preorder') {
        return {
          ...commonParams,
          preorder: true
        }
      }
      if (this.selectedInventoryList?.id === 'orderSoon') {
        return {
          ...commonParams,
          order_soon: true
        }
      }
      if (this.selectedInventoryList?.id === 'outOfStock') {
        return {
          ...commonParams,
          inventory_qty__lte: 0
        }
      }
      return {
        ...commonParams,
        inventorylist: this.selectedInventoryList?.id
      }
    },
    injectItems () {
      return this.selected.map(item => item.detail)
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    inventoriesPageChanged () {
      this.fetchInventories()
    },
    async fetchInventories () {
      try {
        this.inventories.isFetching = true
        const endpoint = `/clinics/${this.company.slug}/inventories`
        const { data } = await this.axios.get(endpoint, { params: this.inventoriesParams })
        this.inventories.totalCount = data.count
        this.inventories.data = data.results.map(result => this.sanitizeData(result))
      } catch (error) {
        this.inventories.isError = true
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.inventories.isFetching = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchInventories()
    }, 750),
    handleSelectAll (value) {
      if (value) {
        this.selected = [...this.selected, ...this.inventories.data]
      } else {
        const productsId = this.inventories.data.map(item => item.id)
        this.selected = this.selected.filter(item => !productsId.includes(item.id))
      }
    },
    handleRemoveItem (item) {
      this.selected = this.selected.filter(selectedItem => selectedItem.id !== item.id)
    },
    handleRemoveAllItem () {
      this.selected = []
    },
    sanitizeData (data) {
      return {
        detail: data,
        id: data.id,
        name: data.name,
        uom: data.uom ?? '-',
        lastPrice: data.last_price,
        inventory_qty: data.inventory_qty,
        available_qty: data.available_qty || 0,
        to_order_qty: data.to_order_qty || 0,
        last7d: data.usage['7_days'],
        last14d: data.usage['14_days'],
        last30d: data.usage['30_days'],
      }
    },
    handleCreate (selectedDocument) {
      this.selectedDocument = {
        id: selectedDocument
      }
      this.openDocumentDialog = true
    },
    handleUpdatedDocument () {
      this.handleRemoveAllItem()
      this.$emit('updated')
    },
    getInventoryStatus (inventoryQty, last14d) {
      if (inventoryQty === 0) {
        return 'Out of stock'
      }
      if (inventoryQty < last14d) {
        return 'Order soon'
      }
      return 'Sufficient'
    },
    getInventoryStatusTextColor (inventoryQty, last14d) {
      if (inventoryQty === 0 || inventoryQty < last14d) {
        return 'danger500--text'
      }
      return 'success500--text'
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
  },
}
</script>
  
<style scoped>
.to-bottom {
  position: absolute;
  bottom: 0;
  width: 100%
}
.overflow-selection {
  max-height: 600px;
  overflow-y: auto;
}
</style>
